import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { getLiturgicalSeason, getSeasonLogo } from "@/lib/liturgicalCalendar";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [season] = useState(() => getLiturgicalSeason());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        toast.error(data.detail || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img 
            src={getSeasonLogo(season)} 
            alt="Parish" 
            className="w-11 h-11 rounded-lg transition-all duration-500"
          />
          <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
        </div>

        <Card className="bg-white border-[#E5E0D5] shadow-lg" data-testid="forgot-password-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">
              {submitted ? "Check Your Email" : "Forgot Password"}
            </CardTitle>
            <CardDescription className="font-['Nunito'] text-[#6B665E]">
              {submitted 
                ? "We've sent you a password reset link" 
                : "Enter your email and we'll send you a reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {submitted ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <p className="text-[#6B665E] font-['Nunito']">
                  If an account exists for <strong>{email}</strong>, you'll receive an email with instructions to reset your password.
                </p>
                <p className="text-sm text-[#6B665E] font-['Nunito']">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="font-semibold hover:underline"
                    style={{ color: season.colors.primary }}
                  >
                    try again
                  </button>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito']"
                      data-testid="forgot-password-email-input"
                      required
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full text-white font-['Nunito'] font-semibold h-11 active:scale-95 transition-all"
                  style={{ backgroundColor: season.colors.primary }}
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            )}

            <div className="pt-2">
              <Link 
                to="/login" 
                className="flex items-center justify-center gap-2 text-sm font-['Nunito'] hover:underline"
                style={{ color: season.colors.primary }}
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
